// src/whatsappChat/components/InteractiveBuilder.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { isHttpsUrl } from '../api/content';
import { Field } from './Modal';

const KINDS = [
  { value: 'button', label: 'Reply buttons' },
  { value: 'list', label: 'List' },
  { value: 'cta_url', label: 'Link button' },
];

const LIMITS = { text: 1024, header: 60, footer: 60, button: 20, listButton: 20, rowTitle: 24, rowDesc: 72 };
const MAX_BUTTONS = 3;
const MAX_ROWS = 10;

// Ids only need to be unique within the message; the customer sees the title.
const makeId = (prefix, index, title) =>
  `${prefix}_${index + 1}_${String(title).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 30)}`;

/** Builds the interactive fields of POST /messages and hands them to onSend. */
export function InteractiveBuilder({ sending, onSend, onCancel }) {
  const [kind, setKind] = useState('button');
  const [text, setText] = useState('');
  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState(['', '']);
  const [listButton, setListButton] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [rows, setRows] = useState([{ title: '', description: '' }]);
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const errors = {};
  if (!text.trim()) errors.text = 'Required';
  else if (text.length > LIMITS.text) errors.text = `At most ${LIMITS.text} characters`;

  if (kind === 'button') {
    const titles = buttons.map((b) => b.trim());
    titles.forEach((title, i) => {
      if (!title) errors[`button-${i}`] = 'Required';
      else if (title.length > LIMITS.button) errors[`button-${i}`] = `At most ${LIMITS.button} characters`;
      else if (titles.indexOf(title) !== i) errors[`button-${i}`] = 'Each button needs a different title';
    });
    if (header.length > LIMITS.header) errors.header = `At most ${LIMITS.header} characters`;
    if (footer.length > LIMITS.footer) errors.footer = `At most ${LIMITS.footer} characters`;
  }

  if (kind === 'list') {
    if (!listButton.trim()) errors.listButton = 'Required';
    else if (listButton.length > LIMITS.listButton) errors.listButton = `At most ${LIMITS.listButton} characters`;
    if (sectionTitle.length > LIMITS.rowTitle) errors.sectionTitle = `At most ${LIMITS.rowTitle} characters`;
    rows.forEach((row, i) => {
      if (!row.title.trim()) errors[`row-${i}`] = 'Title is required';
      else if (row.title.length > LIMITS.rowTitle) errors[`row-${i}`] = `Title: at most ${LIMITS.rowTitle} characters`;
      else if (row.description.length > LIMITS.rowDesc) errors[`row-${i}`] = `Description: at most ${LIMITS.rowDesc} characters`;
    });
  }

  if (kind === 'cta_url') {
    if (!ctaText.trim()) errors.ctaText = 'Required';
    else if (ctaText.length > LIMITS.button) errors.ctaText = `At most ${LIMITS.button} characters`;
    if (!isHttpsUrl(ctaUrl)) errors.ctaUrl = 'Enter an https link';
  }

  const err = (key) => (showErrors ? errors[key] : null);

  const submit = (e) => {
    e.preventDefault();
    if (sending) return;
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }

    const payload = { type: 'interactive', interactive_type: kind, text: text.trim() };
    if (kind === 'button') {
      payload.buttons = buttons.map((title, i) => ({ id: makeId('btn', i, title), title: title.trim() }));
      if (header.trim()) payload.header_text = header.trim();
      if (footer.trim()) payload.footer_text = footer.trim();
    } else if (kind === 'list') {
      payload.list_button = listButton.trim();
      payload.sections = [
        {
          ...(sectionTitle.trim() ? { title: sectionTitle.trim() } : {}),
          rows: rows.map((row, i) => ({
            id: makeId('row', i, row.title),
            title: row.title.trim(),
            ...(row.description.trim() ? { description: row.description.trim() } : {}),
          })),
        },
      ];
    } else {
      payload.cta_text = ctaText.trim();
      payload.cta_url = ctaUrl.trim();
    }
    onSend(payload);
  };

  const setRow = (index, patch) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <form className="wa-form" onSubmit={submit}>
      <div className="wa-tabs" role="tablist">
        {KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            role="tab"
            aria-selected={kind === k.value}
            className={`wa-tab${kind === k.value ? ' is-active' : ''}`}
            onClick={() => {
              setKind(k.value);
              setShowErrors(false);
            }}
          >
            {k.label}
          </button>
        ))}
      </div>

      {kind === 'button' && (
        <Field label="Header (optional)" count={header.length} max={LIMITS.header} error={err('header')}>
          <input className="wa-input" type="text" value={header} onChange={(e) => setHeader(e.target.value)} />
        </Field>
      )}

      <Field label="Message" count={text.length} max={LIMITS.text} error={err('text')}>
        <textarea className="wa-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>

      {kind === 'button' && (
        <>
          <Field label="Footer (optional)" count={footer.length} max={LIMITS.footer} error={err('footer')}>
            <input className="wa-input" type="text" value={footer} onChange={(e) => setFooter(e.target.value)} />
          </Field>

          {buttons.map((title, i) => (
            <Field
              key={i}
              label={`Button ${i + 1}`}
              count={title.length}
              max={LIMITS.button}
              error={err(`button-${i}`)}
            >
              <span className="wa-input-row">
                <input
                  className="wa-input"
                  type="text"
                  value={title}
                  onChange={(e) => setButtons((prev) => prev.map((b, idx) => (idx === i ? e.target.value : b)))}
                />
                {buttons.length > 1 && (
                  <button
                    type="button"
                    className="wa-icon-btn"
                    aria-label={`Remove button ${i + 1}`}
                    onClick={() => setButtons((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </span>
            </Field>
          ))}
          {buttons.length < MAX_BUTTONS && (
            <button type="button" className="wa-btn wa-btn-secondary wa-btn-add" onClick={() => setButtons((prev) => [...prev, ''])}>
              <FontAwesomeIcon icon={faPlus} /> Add button
            </button>
          )}
        </>
      )}

      {kind === 'list' && (
        <>
          <Field
            label="Button that opens the list"
            count={listButton.length}
            max={LIMITS.listButton}
            error={err('listButton')}
          >
            <input className="wa-input" type="text" value={listButton} onChange={(e) => setListButton(e.target.value)} />
          </Field>
          <Field label="Section title (optional)" count={sectionTitle.length} max={LIMITS.rowTitle} error={err('sectionTitle')}>
            <input className="wa-input" type="text" value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} />
          </Field>

          {rows.map((row, i) => (
            <Field key={i} label={`Row ${i + 1}`} error={err(`row-${i}`)}>
              <span className="wa-input-row">
                <input
                  className="wa-input"
                  type="text"
                  placeholder="Title"
                  value={row.title}
                  onChange={(e) => setRow(i, { title: e.target.value })}
                />
                <input
                  className="wa-input"
                  type="text"
                  placeholder="Description (optional)"
                  value={row.description}
                  onChange={(e) => setRow(i, { description: e.target.value })}
                />
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="wa-icon-btn"
                    aria-label={`Remove row ${i + 1}`}
                    onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </span>
            </Field>
          ))}
          {rows.length < MAX_ROWS && (
            <button
              type="button"
              className="wa-btn wa-btn-secondary wa-btn-add"
              onClick={() => setRows((prev) => [...prev, { title: '', description: '' }])}
            >
              <FontAwesomeIcon icon={faPlus} /> Add row
            </button>
          )}
        </>
      )}

      {kind === 'cta_url' && (
        <>
          <Field label="Button text" count={ctaText.length} max={LIMITS.button} error={err('ctaText')}>
            <input className="wa-input" type="text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} />
          </Field>
          <Field label="Link" error={err('ctaUrl')}>
            <input
              className="wa-input"
              type="url"
              value={ctaUrl}
              onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="https://"
            />
          </Field>
        </>
      )}

      <div className="wa-modal-actions">
        <button type="button" className="wa-btn wa-btn-secondary" onClick={onCancel} disabled={sending}>
          Cancel
        </button>
        <button type="submit" className="wa-btn wa-btn-primary" disabled={sending}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </form>
  );
}
